//---------------------------------------------------------------------------------------------------
//  ?                              TRADING ANALYTICS
//  @author         :  DeeTrade Team
//  @createdOn      :  04/10/2026
//  @description    :  Client-side trading analytics — matched-trade P&Ls (drawdown/Sharpe live in OMS /algo/summary)
//---------------------------------------------------------------------------------------------------

// Component: tradingAnalytics.js
// Purpose: Compute matched-trade P&Ls from live order history

/*>>> HELPERS <<<*/

/*>>> MAIN EXPORTS <<<*/

/**
 * Compute performance metrics from filled orders — canonical chronological-flip algorithm.
 *
 * Mirrors backend performance_calculator.compute_performance exactly:
 * - Walk orders in chronological order (sorted by _epoch ascending)
 * - Filter to FILLED orders only
 * - Track pendingEntry: when we see an opposite-side order, it closes a round-trip trade
 * - Same-side orders update the pending entry (scale-in behavior)
 *
 * IMPORTANT: This function assumes all orders are from a SINGLE strategy_instance_id.
 * For multi-SII aggregation, use computeAnalytics instead. Mixed-SII input is not
 * silently aggregated — it triggers a console.warn (development warning, not an
 * exception, to avoid breaking pages that historically tolerated mixed input).
 *
 * Each order must have:
 *   order_id, strategy_instance_id, side, qty, filled_price, filled_qty, status, _epoch
 *
 * Contract-multiplier resolution priority (most specific wins) — order-quantity-canonicalization
 * (D9): this is a VALUE multiplier, never lot_size (display-only granularity):
 *   1. pendingEntry.contract_multiplier — the multiplier at position-open time (most accurate)
 *   2. order.contract_multiplier — exit-side fallback if entry is missing the field
 *   3. options.multiplier — caller-supplied override (single-SII convenience)
 *   4. 1 — spot/equity default
 * Multi-SII callers (computeAnalytics) DO NOT pass options.multiplier and rely on
 * per-order contract_multiplier flowing through the WS payload (project_oms_commit3_cutover).
 *
 * @param {Array|null|undefined} orders
 * @param {object} options
 * @param {number|null} options.realizedPnlOverride — when truthy, use as totalPnl instead of sum(tradePnls)
 * @param {number} options.multiplier — fallback multiplier when neither order row carries one (default 1)
 * @returns {{
 *   totalTrades: number,
 *   winningTrades: number,
 *   losingTrades: number,
 *   winRate: number,
 *   totalPnl: number,
 *   avgPnlPerTrade: number,
 *   maxWin: number,
 *   maxLoss: number,
 *   tradePnls: number[]
 * }}
 */
export const computePerformance = (orders, { realizedPnlOverride = null, multiplier = 1 } = {}) => {
  //! Default result — returned on empty / invalid input
  const empty = {
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    winRate: 0,
    totalPnl: 0,
    avgPnlPerTrade: 0,
    maxWin: 0,
    maxLoss: 0,
    tradePnls: [],
  };

  if (!orders || !orders.length) return empty;

  //! Mixed-SII guard — pairing across strategies would close trades wrongly
  //!  (a SELL on strategy A could close a BUY on strategy B). computeAnalytics
  //!  is the multi-SII safe wrapper. Warn loudly so misuse surfaces in dev
  //!  console; we don't throw because production pages may have benign mixed
  //!  arrays (e.g., admin views) where the result is acceptable noise.
  if (orders.length > 1) {
    const firstSii = orders[0]?.strategy_instance_id;
    const mixed = orders.some((o) => o.strategy_instance_id !== firstSii);
    if (mixed) {
      // eslint-disable-next-line no-console
      console.warn(
        '[tradingAnalytics] computePerformance received orders from multiple strategy_instance_ids. ' +
        'Pairings will be wrong. Use computeAnalytics for multi-SII input.'
      );
    }
  }

  /*>>> STEP 1: Sort orders chronologically by _epoch ascending <<<*/
  const sortedOrders = [...orders].sort((a, b) => a._epoch - b._epoch);

  /*>>> STEP 2: Pair orders chronologically — opposite-side flip closes a trade <<<*/
  //* Mirror backend's algorithm: walk in time order, track pendingEntry,
  //* opposite-side flip closes the trade, same-side updates entry
  const tradePnls = [];
  let pendingEntry = null;

  for (const order of sortedOrders) {
    //--> Skip non-FILLED orders
    if (order.status !== 'FILLED') continue;

    if (pendingEntry === null) {
      //--> First order or after a closed trade — this is an entry
      pendingEntry = order;
    } else if (order.side !== pendingEntry.side) {
      //--> Opposite side — this closes the trade
      const entryPrice = parseFloat(pendingEntry.filled_price);
      const exitPrice = parseFloat(order.filled_price);
      const qty = parseFloat(order.filled_qty || order.qty);
      //! Per-order contract_multiplier — F&O instruments (CRUDEOIL=100, NIFTY=50) carry
      //!  the multiplier on each order row. Falling back to the option-passed
      //!  multiplier (single-SII caller convenience) and finally 1 for spot.
      //!  Multi-SII callers (computeAnalytics) MUST rely on per-order to mix
      //!  instruments correctly without a global option.
      const tradeMultiplier = parseFloat(
        pendingEntry.contract_multiplier || order.contract_multiplier || multiplier || 1
      );

      let pnl;
      if (pendingEntry.side === 'BUY') {
        //* BUY entry: profit when price rises
        pnl = (exitPrice - entryPrice) * qty * tradeMultiplier;
      } else {
        //* SELL entry (short): profit when price falls
        pnl = (entryPrice - exitPrice) * qty * tradeMultiplier;
      }

      tradePnls.push(pnl);
      pendingEntry = null;
    } else {
      //--> Same side (e.g. adding to position) — update entry to latest
      pendingEntry = order;
    }
  }

  /*>>> STEP 3: Aggregate metrics <<<*/
  const totalTrades = tradePnls.length;
  const winningTrades = tradePnls.filter((p) => p > 0).length;
  const losingTrades = tradePnls.filter((p) => p < 0).length;
  const winRate = totalTrades > 0 ? winningTrades / totalTrades : 0;

  //--> Use realizedPnlOverride if provided (mirrors backend priority rule), else sum(tradePnls)
  const computedPnl = tradePnls.reduce((sum, p) => sum + p, 0);
  const totalPnl = realizedPnlOverride != null ? realizedPnlOverride : computedPnl;

  const avgPnlPerTrade = totalTrades > 0 ? totalPnl / totalTrades : 0;

  const maxWin = tradePnls.length > 0 ? Math.max(...tradePnls.filter((p) => p > 0), 0) : 0;
  const maxLoss = tradePnls.length > 0 ? Math.min(...tradePnls.filter((p) => p < 0), 0) : 0;

  return {
    totalTrades,
    winningTrades,
    losingTrades,
    winRate,
    totalPnl,
    avgPnlPerTrade,
    maxWin,
    maxLoss,
    tradePnls,
  };
};

/**
 * Compute matched-trade P&Ls (tradePnls) from a flat array of Order objects.
 *
 * Uses the canonical chronological-flip pairing algorithm, with per-SII grouping to handle
 * multiple strategy instances in the same array (for backwards compatibility with the original
 * computeAnalytics which had entryIndex Map<SII → entries[]>).
 *
 * Each order must have:
 *   order_id, strategy_instance_id, side, qty, filled_price, filled_qty, status, _epoch
 *
 * @param {Array|null|undefined} orders
 * @returns {{ tradePnls: number[] }}
 */
export const computeAnalytics = (orders) => {
  //! Default result — returned on empty / invalid input
  const empty = { tradePnls: [] };

  if (!orders || !orders.length) return empty;

  /*>>> STEP 1: Group orders by strategy_instance_id <<<*/
  const ordersBySii = new Map();
  for (const order of orders) {
    const sii = order.strategy_instance_id;
    if (!ordersBySii.has(sii)) ordersBySii.set(sii, []);
    ordersBySii.get(sii).push(order);
  }

  /*>>> STEP 2: Pair trades within each SII, aggregate all tradePnls <<<*/
  const allTradePnls = [];

  for (const [, ordersForSii] of ordersBySii) {
    //--> Use computePerformance for single-SII pairing
    const perf = computePerformance(ordersForSii, {});
    allTradePnls.push(...perf.tradePnls);
  }

  if (!allTradePnls.length) return empty;

  return { tradePnls: allTradePnls };
};
